/**
 * 端末識別子 (#819 Track B-main Phase 1).
 *
 * **credential ではなく独立した device-id** をこのブラウザに 1 つ発行し
 * localStorage に永続化する(設計判断①: 同期 passkey を持つ攻撃者端末を
 * 弾くため、識別子は credential と分離する)。session/logout を跨いで
 * **保持し続ける**(= 再訪した同一端末を「既知」と認識するのが目的なので
 * logout でクリアしない)。
 *
 * Phase 1 は「発行 + サーバ記録」まで。初見端末の gate/承認は Phase 2。
 *
 * 注意: 本機能の対象は WebAuthn 不可も含む**古い端末**なので、
 * `crypto.randomUUID` が無い環境の fallback を必ず持つ。
 */
const KEY = 'yokoso_device_id'

/** UUID v4 (crypto.randomUUID の無い古い端末・http の場所でも作れる)。 */
export function uuidv4(): string {
  // 1) 標準 (modern browsers)
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto
    if (c && typeof c.randomUUID === 'function') return c.randomUUID()
    if (c && typeof c.getRandomValues === 'function') {
      const b = c.getRandomValues(new Uint8Array(16))
      b[6] = (b[6] & 0x0f) | 0x40
      b[8] = (b[8] & 0x3f) | 0x80
      const h = Array.from(b, (x) => x.toString(16).padStart(2, '0'))
      return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h
        .slice(6, 8)
        .join('')}-${h.slice(8, 10).join('')}-${h.slice(10, 16).join('')}`
    }
  } catch {
    /* fall through to non-crypto fallback (古い端末) */
  }
  // 2) 非 crypto fallback (識別子であり鍵ではないので強度要件は無い)
  return (
    'dev-' +
    Date.now().toString(36) +
    '-' +
    Math.random().toString(36).slice(2, 12) +
    Math.random().toString(36).slice(2, 12)
  )
}

/** このブラウザの安定 device-id を取得 (無ければ発行して永続化)。 */
export function getDeviceId(): string {
  try {
    let id = localStorage.getItem(KEY)
    if (!id) {
      id = uuidv4()
      localStorage.setItem(KEY, id)
    }
    return id
  } catch {
    // localStorage 不可 (private mode 等) — session 内一時 id にフォールバック。
    // 永続しないので毎回「初見」扱いになるが、誤許可より安全側 (Phase 2 で
    // 承認を要求する方向に倒れる)。
    return uuidv4()
  }
}

/** 明示的な「この端末を忘れる」用 (Phase 3 想定)。logout では呼ばない。 */
export function clearDeviceId(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    /* no-op */
  }
}

/**
 * #841 bug fix: logout / deleteAccount / resetState の localStorage 全消去で
 * **device-id を保持**する。device-id は browser 固有の安定識別子で
 * session/account に紐づかない(本ファイル冒頭の設計①、明示的な忘却は
 * `clearDeviceId` のみ=Phase 3 想定)。素の `localStorage.clear()` だと
 * logout 毎に device-id が再生成され「新端末」が無限増殖し、承認 gate
 * (#819)/ refined-(a)(#837)/ 端末管理(#841)の前提が根本から崩れる。
 */
export function clearLocalStoragePreservingDeviceId(): void {
  let did: string | null = null
  try {
    did = localStorage.getItem(KEY)
  } catch {
    /* ignore */
  }
  try {
    localStorage.clear()
  } catch {
    /* ignore */
  }
  if (did) {
    try {
      localStorage.setItem(KEY, did)
    } catch {
      /* ignore */
    }
  }
}
